/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react'
import {
    Button,
    HStack,
    Input,
    useNumberInput,
    Fade,
    Box,
    Link,
    Text,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {FormattedMessage, useIntl} from 'react-intl'
import PropTypes from 'prop-types'
import {useCustomerServiceUrl} from '../../hooks/use-customer-service-url'
import {MinusIcon, AddIcon, ErrorIcon} from '../icons'

/**
 * This is the mobile implementation of the Chakra NumberInput. This simple component essentially
 * is a helper so we don't have to reuse the hooks every time we need a number input since design dictates
 * we use the moobile variation on all screens.
 *
 * NOTE: We can optionally put global logic we see if in here, and various styling decisions in this single
 * component.
 *
 * @param {*} props
 * @returns
 */
const QuantityPicker = (props) => {
    const styles = useMultiStyleConfig('QuantityPicker')
    const intl = useIntl()
    const customerServiceUrl = useCustomerServiceUrl()
    const productName = props.productName
    const {getInputProps, getIncrementButtonProps, getDecrementButtonProps} = useNumberInput({
        ...props,
        // Defaults
        focusInputOnChange: false,
        onFocus: (e) => {
            // eslint-disable-next-line react/prop-types
            const {onFocus} = props

            // This is useful for mobile devices, this allows the user to pop open the keyboard and set the
            // new quantity with one click.
            e.target.select()

            // If there is a `onFocus` property define, call it with the event captured.
            // eslint-disable-next-line react/prop-types
            onFocus?.call(this, e)
        }
    })

    const inc = getIncrementButtonProps({
        variant: 'quantity-picker',
        'aria-label': intl.formatMessage(
            {
                defaultMessage: 'Increment Quantity for {productName}',
                id: 'product_view.label.assistive_msg.quantity_increment'
            },
            {productName}
        )
    })
    const dec = getDecrementButtonProps({
        variant: 'quantity-picker',
        'aria-label': intl.formatMessage(
            {
                defaultMessage: 'Decrement Quantity for {productName}',
                id: 'product_view.label.assistive_msg.quantity_decrement'
            },
            {productName}
        )
    })
    const input = getInputProps({
        variant: 'quantity-picker',
        'aria-label': intl.formatMessage({
            defaultMessage: 'Quantity',
            id: 'product_view.label.quantity'
        })
    })

    // Clamp typed values to max quantity
    const originalOnChange = input.onChange
    input.onChange = (e) => {
        const val = parseInt(e.target.value, 10)
        if (props.max && val > props.max) {
            e.target.value = String(props.max)
        }
        originalOnChange?.(e)
    }

    // Accessibility improvements:
    // 1. Allow keyboard focus on the buttons - Chakra overrides values passed to get*ButtonProps()
    inc.tabIndex = ''
    dec.tabIndex = ''
    // 2. Allow Space or Enter key to trigger buttons
    // Hitting space/enter triggers a "click" event, but the component listens for "mousedown".
    // We can't reuse the buttons' onMouseDown handler, so instead we hijack the input's onKeyDown.
    // @ref https://github.com/chakra-ui/chakra-ui/blob/%40chakra-ui/react%402.7.0/packages/components/number-input/src/use-number-input.ts#L333-L334
    inc.onKeyDown = (evt) => {
        if (evt.key === ' ' || evt.key === 'Enter') {
            evt.key = 'ArrowUp'
            input.onKeyDown(evt)
        }
    }
    dec.onKeyDown = (evt) => {
        if (evt.key === ' ' || evt.key === 'Enter') {
            evt.key = 'ArrowDown'
            input.onKeyDown(evt)
        }
    }

    const isAtMin = Number(props.value) <= 1
    const isAtMax = props.max !== 0 && Number(props.value) >= props.max

    return (
        <>
            <HStack {...styles.container} {...props}>
                <Button
                    data-testid="cart-button-quantity-decrement"
                    {...dec}
                    {...styles.btn}
                    isDisabled={isAtMin || dec.disabled}
                    bg={isAtMin ? 'webTheme.fieldDisabled' : undefined}
                    color={isAtMin ? 'webTheme.iconDisabled' : 'webTheme.iconPrimary'}
                >
                    <MinusIcon boxSize={5} />
                </Button>
                <Input {...input} />
                <Button data-testid="cart-button-quantity-increment" {...inc} {...styles.btn} isDisabled={isAtMax || inc.disabled} bg={isAtMax ? 'webTheme.fieldDisabled' : undefined} color={isAtMax ? 'webTheme.iconDisabled' : 'webTheme.iconPrimary'}>
                    <AddIcon boxSize={5} />
                </Button>
            </HStack>
            <Box>
                {props.max !== 0 && Number(props.value) >= props.max && (
                    <Fade in={true}>
                        <HStack {...styles.maxMessage}>
                            <ErrorIcon boxSize={5} />
                            <Text>
                                <FormattedMessage
                                    defaultMessage="You've reached the maximum quantity for this item. Need help? <customerService>Contact Customer Service</customerService>."
                                    id="quantity.text.max_quantity_error_message"
                                    values={{
                                        customerService: (chunks) => (
                                            <Link
                                                href={customerServiceUrl}
                                                textDecoration="underline"
                                                color="inherit"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                {chunks}
                                            </Link>
                                        )
                                    }}
                                />
                            </Text>
                        </HStack>
                    </Fade>
                )}
            </Box>
        </>
    )
}

QuantityPicker.propTypes = {
    productName: PropTypes.string,
    value: PropTypes.number,
    max: PropTypes.number
}

export default QuantityPicker
